import React, {useEffect, useRef} from 'react';

const PARTICLE_COLORS = [
    'rgba(99, 102, 241,',
    'rgba(139, 92, 246,',
    'rgba(167, 139, 250,',
    'rgba(79, 70, 229,',
];

const AURORA_LAYERS = [
    { yFrac: 0.30, amplitude: 95,  waveFreq: 0.0025, timeFreq: 0.00045, thickness: 210, r: 99,  g: 102, b: 241, alpha: 0.07  },
    { yFrac: 0.52, amplitude: 75,  waveFreq: 0.0032, timeFreq: 0.00030, thickness: 180, r: 139, g: 92,  b: 246, alpha: 0.06  },
    { yFrac: 0.18, amplitude: 80,  waveFreq: 0.0018, timeFreq: 0.00035, thickness: 190, r: 79,  g: 70,  b: 229, alpha: 0.055 },
];

const AppBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const particles = Array.from({length: 65}, () => ({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            r: Math.random() * 2.2 + 0.6,
            speed: Math.random() * 0.5 + 0.2,
            opacity: Math.random() * 0.35 + 0.1,
            opacityDir: Math.random() > 0.5 ? 1 : -1,
            opacitySpeed: Math.random() * 0.005 + 0.001,
            color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
        }));

        let t = 0;
        let animId;

        const drawAuroraLayer = (layer) => {
            const w = canvas.width;
            const h = canvas.height;
            const baseY = layer.yFrac * h;
            const th = layer.thickness;

            const points = [];
            for (let x = 0; x <= w; x += 5) {
                const y = baseY
                    + Math.sin(x * layer.waveFreq + t * layer.timeFreq) * layer.amplitude
                    + Math.sin(x * layer.waveFreq * 2.3 + t * layer.timeFreq * 1.6 + 1.2) * layer.amplitude * 0.3
                    + Math.sin(x * layer.waveFreq * 0.45 + t * layer.timeFreq * 0.65 + 2.5) * layer.amplitude * 0.5;
                points.push([x, y]);
            }

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(points[0][0], points[0][1] - th);
            for (const [x, y] of points) ctx.lineTo(x, y - th);
            for (let i = points.length - 1; i >= 0; i--) {
                ctx.lineTo(points[i][0], points[i][1] + th);
            }
            ctx.closePath();

            const grad = ctx.createLinearGradient(0, baseY - th, 0, baseY + th);
            grad.addColorStop(0,    `rgba(${layer.r},${layer.g},${layer.b},0)`);
            grad.addColorStop(0.35, `rgba(${layer.r},${layer.g},${layer.b},${layer.alpha})`);
            grad.addColorStop(0.65, `rgba(${layer.r},${layer.g},${layer.b},${layer.alpha})`);
            grad.addColorStop(1,    `rgba(${layer.r},${layer.g},${layer.b},0)`);

            ctx.fillStyle = grad;
            ctx.fill();
            ctx.restore();
        };

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            AURORA_LAYERS.forEach(drawAuroraLayer);

            particles.forEach(p => {
                p.y += p.speed;
                p.opacity += p.opacityDir * p.opacitySpeed;
                if (p.opacity > 0.45 || p.opacity < 0.06) p.opacityDir *= -1;
                if (p.y > canvas.height + 10) {
                    p.y = -10;
                    p.x = Math.random() * canvas.width;
                }
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `${p.color}${p.opacity})`;
                ctx.shadowBlur = 6;
                ctx.shadowColor = `${p.color}0.6)`;
                ctx.fill();
                ctx.shadowBlur = 0;
            });

            t++;
            animId = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return (
        <div className="app-bg-orbs" aria-hidden="true">
            <canvas ref={canvasRef} className="app-bg-canvas" />
        </div>
    );
};

export default AppBackground;
